// utils/relationships/detectRelationshipStateUpdate.js

function normalizeText(value = '') {
  return String(value)
    .toLowerCase()
    .trim()
    .replace(/[.,!?]+$/g, '');
}

function escapeRegex(value = '') {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function buildNameMatchers(firstName = '', lastName = '') {
  const first = normalizeText(firstName);
  const last = normalizeText(lastName);

  const patterns = [];

  if (first && last) {
    patterns.push(new RegExp(`\\b${escapeRegex(first)}\\s+${escapeRegex(last)}\\b`, 'i'));
  }

  if (first) {
    patterns.push(new RegExp(`\\b${escapeRegex(first)}\\b`, 'i'));
  }

  return patterns;
}

function detectLifeState(message = '') {
  const lower = normalizeText(message);

  const deceasedPatterns = [
    /\bis deceased\b/i,
    /\bpassed away\b/i,
    /\bhas passed away\b/i,
    /\bdied\b/i,
    /\bis dead\b/i,
    /\bwas killed\b/i,
    /\bgot killed\b/i,
  ];

  for (const pattern of deceasedPatterns) {
    if (pattern.test(lower)) {
      return {
        field: 'lifeState',
        value: 'deceased',
        confidence: 0.95,
        matchedBy: pattern.toString(),
      };
    }
  }

  return null;
}

function detectHealthState(message = '') {
  const lower = normalizeText(message);

  const healthMatchers = [
    { pattern: /\bis sick\b/i, value: 'ill' },
    { pattern: /\bis ill\b/i, value: 'ill' },
    { pattern: /\bis very sick\b/i, value: 'ill' },
    { pattern: /\bis in the hospital\b/i, value: 'ill' },
    { pattern: /\bis hospitalized\b/i, value: 'ill' },
    { pattern: /\bis handicapped\b/i, value: 'disabled' },
    { pattern: /\bis disabled\b/i, value: 'disabled' },
    { pattern: /\bhas cancer\b/i, value: 'ill' },
    { pattern: /\bis chronically ill\b/i, value: 'chronic' },
  ];

  for (const matcher of healthMatchers) {
    if (matcher.pattern.test(lower)) {
      return {
        field: 'healthState',
        value: matcher.value,
        confidence: 0.9,
        matchedBy: matcher.pattern.toString(),
      };
    }
  }

  return null;
}

function detectLocation(message = '') {
  const lower = normalizeText(message);

  const locationPatterns = [
    /\blives in ([a-zA-Z\s'.-]+)$/i,
    /\bis in ([a-zA-Z\s'.-]+)$/i,
    /\bmoved to ([a-zA-Z\s'.-]+)$/i,
  ];

  for (const pattern of locationPatterns) {
    const match = lower.match(pattern);
    if (match?.[1]) {
      const location = match[1].trim();
      if (location) {
        return {
          field: 'location',
          value: location.replace(/\s+/g, ' '),
          confidence: 0.85,
          matchedBy: pattern.toString(),
        };
      }
    }
  }

  return null;
}

function buildRelationshipMatch({
  person,
  bindingType,
  bindingConfidence,
  matchedBy,
}) {
  if (!person?.first_name || !person?.relationship) return null;

  const firstName = person.first_name;
  const lastName = person.last_name || null;
  const relationship = person.relationship;
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();

  return {
    entityId: person?.entityId || null,
    first_name: firstName,
    last_name: lastName,
    relationship,
    fullName,
    sourceRelationship: person,
    bindingType,
    bindingConfidence,
    bindingMatchedBy: matchedBy,
  };
}

function findMatchedRelationshipByName(message = '', relationships = []) {
  if (!Array.isArray(relationships) || !relationships.length) return null;

  const lower = normalizeText(message);
  let bestMatch = null;

  for (const person of relationships) {
    const firstName = person?.first_name || '';
    const lastName = person?.last_name || '';
    const relationship = person?.relationship || '';

    if (!firstName || !relationship) continue;

    const nameMatchers = buildNameMatchers(firstName, lastName);
    const matchedByName = nameMatchers.some((pattern) => pattern.test(lower));

    if (!matchedByName) continue;

    bestMatch = buildRelationshipMatch({
      person,
      bindingType: 'explicit_name',
      bindingConfidence: 0.98,
      matchedBy: 'explicit_name',
    });

    if (
      lastName &&
      new RegExp(`\\b${escapeRegex(firstName)}\\s+${escapeRegex(lastName)}\\b`, 'i').test(lower)
    ) {
      return bestMatch;
    }
  }

  return bestMatch;
}

function rolePhrasePattern(role = '') {
  const normalizedRole = normalizeText(role);
  if (!normalizedRole) return null;

  const escaped = escapeRegex(normalizedRole).replace(/\\-/g, '[-\\s]?');

  return new RegExp(`\\bmy\\s+(?:only\\s+)?${escaped}\\b`, 'i');
}

function findMatchedRelationshipByRole(message = '', relationships = []) {
  if (!Array.isArray(relationships) || !relationships.length) return null;

  const lower = normalizeText(message);
  const matched = [];

  for (const person of relationships) {
    const role = person?.relationship || '';
    if (!role) continue;

    const pattern = rolePhrasePattern(role);
    if (!pattern) continue;

    if (pattern.test(lower)) {
      matched.push(
        buildRelationshipMatch({
          person,
          bindingType: 'relationship_role',
          bindingConfidence: 0.82,
          matchedBy: `role:${role}`,
        })
      );
    }
  }

  if (matched.length === 1) {
    return matched[0];
  }

  return null;
}

function findMatchedRelationshipByLastReference(
  lastReferencedEntity = null,
  relationships = []
) {
  if (!lastReferencedEntity || !Array.isArray(relationships) || !relationships.length) {
    return null;
  }

  const referencedName = normalizeText(lastReferencedEntity?.name || '');
  const referencedRole = normalizeText(lastReferencedEntity?.role || '');

  if (!referencedName) return null;

  const exactRoleMatch = relationships.find((person) => {
    const firstName = normalizeText(person?.first_name || '');
    const role = normalizeText(person?.relationship || '');

    if (!firstName) return false;
    if (firstName !== referencedName) return false;
    if (referencedRole && role && referencedRole !== role) return false;

    return true;
  });

  if (exactRoleMatch) {
    return buildRelationshipMatch({
      person: exactRoleMatch,
      bindingType: 'last_referenced_entity',
      bindingConfidence: 0.8,
      matchedBy: 'last_referenced_entity',
    });
  }

  const nameOnlyMatches = relationships.filter((person) => {
    const firstName = normalizeText(person?.first_name || '');
    return firstName && firstName === referencedName;
  });

  if (nameOnlyMatches.length === 1) {
    return buildRelationshipMatch({
      person: nameOnlyMatches[0],
      bindingType: 'last_referenced_entity',
      bindingConfidence: 0.76,
      matchedBy: 'last_referenced_entity_name_only',
    });
  }

  return null;
}

function findBestRelationshipBinding({
  message = '',
  relationships = [],
  lastReferencedEntity = null,
}) {
  const explicitNameMatch = findMatchedRelationshipByName(message, relationships);
  if (explicitNameMatch) return explicitNameMatch;

  const roleMatch = findMatchedRelationshipByRole(message, relationships);
  if (roleMatch) return roleMatch;

  const lastReferenceMatch = findMatchedRelationshipByLastReference(
    lastReferencedEntity,
    relationships
  );
  if (lastReferenceMatch) return lastReferenceMatch;

  return null;
}

function buildStateUpdateCandidate({
  stateCandidate,
  matchedRelationship,
  sourceText,
}) {
  return {
    type: 'relationship_state_update',
    field: stateCandidate.field,
    value: stateCandidate.value,
    confidence: stateCandidate.confidence,
    matchedBy: stateCandidate.matchedBy,
    bindingType: matchedRelationship.bindingType,
    bindingConfidence: matchedRelationship.bindingConfidence,
    bindingMatchedBy: matchedRelationship.bindingMatchedBy,
    entityId: matchedRelationship.entityId || null,
    first_name: matchedRelationship.first_name,
    last_name: matchedRelationship.last_name,
    relationship: matchedRelationship.relationship,
    fullName: matchedRelationship.fullName,
    sourceRelationship: matchedRelationship.sourceRelationship,
    sourceText,
  };
}

export default function detectRelationshipStateUpdate({
  message = '',
  relationships = [],
  lastReferencedEntity = null,
}) {
  const trimmedMessage = String(message).trim();

  if (!trimmedMessage) return null;

  const lifeStateCandidate = detectLifeState(trimmedMessage);
  const healthStateCandidate = detectHealthState(trimmedMessage);
  const locationCandidate = detectLocation(trimmedMessage);

  const stateCandidate =
    lifeStateCandidate || healthStateCandidate || locationCandidate || null;

  if (!stateCandidate) return null;

  const matchedRelationship = findBestRelationshipBinding({
    message: trimmedMessage,
    relationships,
    lastReferencedEntity,
  });

  if (!matchedRelationship) {
    return null;
  }

  return buildStateUpdateCandidate({
    stateCandidate,
    matchedRelationship,
    sourceText: trimmedMessage,
  });
}